import { CurrencyInput, CurrencySelector, Flex, FormField, MoneyInput } from '@percent/lemonade'
import { CurrencyCode, Money, currencyCodes, currencyMap } from '@percent/utility'
import { CandidateFormQuestion } from '@percent/partner-dashboard/services/grants/grantsService.types'

const ALL_CURRENCIES: CurrencyCode[] = [...currencyCodes]
export const DEFAULT_CURRENCY: CurrencyCode = 'USD'

/**
 * Currency config (`defaultCurrency` / `allowedCurrencies`) is authorable on the `.currency` half only, so that
 * half's config governs whenever it exists. The amount half is still read as a fallback because imported
 * programs skip the cross-field validation and can carry stale config; a validly-authored form never hits it,
 * and an amount-only question therefore falls through to DEFAULT_CURRENCY.
 */
const currencyConfigOf = (currencyQuestion?: CandidateFormQuestion, amountQuestion?: CandidateFormQuestion) => {
  const source = currencyQuestion ?? amountQuestion

  return source?.type === 'money' ? source : undefined
}

/** Money (minor units) or a currency-only change emitted by the Lemonade money inputs. */
type MoneyInputValue = { amount?: number | null; currency: CurrencyCode } | undefined

type MoneyFormFieldProps = {
  amountQuestion?: CandidateFormQuestion
  currencyQuestion?: CandidateFormQuestion
  currencyOnly: boolean
  values: Record<string, string | number | string[] | File[]>
  errors: Record<string, string | undefined>
  touched: Record<string, boolean | undefined>
  onChange: (id: string, value: string) => void
  locale: string
}

const asString = (value: string | number | string[] | File[] | undefined): string | undefined =>
  typeof value === 'string' ? value : typeof value === 'number' ? String(value) : undefined

export function MoneyFormField({
  amountQuestion,
  currencyQuestion,
  currencyOnly,
  values,
  errors,
  touched,
  onChange,
  locale
}: Readonly<MoneyFormFieldProps>) {
  // The question that owns the field's label / required flag / error message.
  const primaryQuestion = amountQuestion ?? currencyQuestion

  if (!primaryQuestion) return null

  const amountId = amountQuestion?.id
  const currencyId = currencyQuestion?.id

  const currencyConfig = currencyConfigOf(currencyQuestion, amountQuestion)
  const currencies = currencyConfig?.allowedCurrencies?.length ? currencyConfig.allowedCurrencies : ALL_CURRENCIES
  const defaultCurrency = currencyConfig?.defaultCurrency ?? DEFAULT_CURRENCY

  const storedCurrency = currencyId ? asString(values[currencyId]) : undefined
  const currency = (storedCurrency as CurrencyCode) || defaultCurrency

  const storedAmount = amountId ? asString(values[amountId]) : undefined
  const amountMinor = storedAmount != null && storedAmount !== '' ? Number(storedAmount) : undefined
  const moneyValue: Money | undefined =
    amountMinor !== undefined && !Number.isNaN(amountMinor) ? { amount: amountMinor, currency } : undefined

  const errorId = amountId ?? currencyId
  const error = errorId && touched[errorId] ? errors[errorId] : undefined

  const writeAmount = (minor: number | undefined) => {
    if (amountId) onChange(amountId, minor === undefined ? '' : String(minor))
  }
  const writeCurrency = (next: CurrencyCode) => {
    if (currencyId) onChange(currencyId, next)
  }

  const minorUnits = currencyMap[currency]?.minorUnits ?? 2

  const handleValueChange = (value: MoneyInputValue, raw: string | undefined) => {
    if (!value) {
      writeAmount(undefined)

      return
    }

    if (value.amount === undefined || value.amount === null) {
      writeCurrency(value.currency)

      return
    }
    // Ignore keystrokes that would introduce more decimals than the currency supports.
    const dotIndex = raw?.indexOf('.') ?? -1
    const decimals = dotIndex === -1 ? 0 : (raw?.length ?? 0) - dotIndex - 1

    if (decimals > minorUnits) return

    writeAmount(value.amount)
    writeCurrency(value.currency)
  }

  const sharedInputProps = {
    id: amountId ?? currencyId,
    name: amountId ?? currencyId,
    locale,
    value: moneyValue,
    placeholder: { amount: 0, currency },
    allowNegativeValue: false,
    onValueChange: handleValueChange
  }

  const renderInput = () => {
    if (currencyOnly) {
      return (
        <CurrencySelector
          defaultCurrency={currency}
          currencies={currencies}
          onValueChange={next => next && writeCurrency(next)}
          data-testid={`q-${currencyId}-currency-select`}
        />
      )
    }

    if (currencyQuestion) {
      return <MoneyInput defaultCurrency={currency} currencies={currencies} {...sharedInputProps} />
    }

    // Amount only: currency is fixed (no selector) — mode (b).
    return <CurrencyInput currency={currency} {...sharedInputProps} />
  }

  return (
    <Flex direction="column" gap={16}>
      <FormField
        label={primaryQuestion.label}
        necessity={primaryQuestion.required ? 'required' : 'optional'}
        status={error ? 'danger' : undefined}
        statusMessage={error}
        description={primaryQuestion.description}
        instructions={primaryQuestion.instructions}
      >
        <Flex flex={1} w="100%">
          {renderInput()}
        </Flex>
      </FormField>
    </Flex>
  )
}
